// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { Pens, RectangleEdgeFilter } from '@shared/commons/Graphics';
import { ProMath } from '../ToolView';
import { SelectionState, ToolSelectionType } from '../Selection';
import { TerceraChartTradingOperation } from '../../Utils/ChartConstants';
import { OrderToolView, CancelCB } from './OrderToolView';
import { TradingToolViewBase, TradingToolViewType } from './TradingToolViewBase';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { OrderFormatter } from '@shared/commons/cache/Formatters/OrderFormatter';
export class StopLimitOrderToolView extends OrderToolView {
    constructor(order, getToolViewByOrderId, terceraChartPanelContext, renderer) {
        super(order, getToolViewByOrderId, terceraChartPanelContext, renderer);
        this.limitIconRect = Rectangle.Empty();
        this.limitIconTextRect = Rectangle.Empty();
        this.limitQuantityRect = Rectangle.Empty();
        this.limitInfoRect = Rectangle.Empty();
        this.limitRemoveButton = Rectangle.Empty();
        this.limitContainerRect = Rectangle.Empty();
        // Move
        this.lastValidLimitPriceValue = NaN;
        this.stopLimitPriceValue = NaN;
        this.invalidPriceDescr = '';
        this.isLinkingStyle = false;
        this.ToolViewType = TradingToolViewType.StopLimitOrderToolView;
    }
    Draw(gr, window, param) {
        var _a;
        if (!((_a = this.Order) === null || _a === void 0 ? void 0 : _a.Price)) {
            return;
        }
        const CR = window.ClientRectangle;
        const minX = CR.X;
        const maxX = minX + CR.Width;
        try {
            const screenPoints00 = this.getCorrectOrderX(this.screenPoints[0][0], minX);
            const screenPoints01 = this.screenPoints[0][1];
            // catch exceptions
            if ((screenPoints00 < 0 && maxX < 0) ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 <= ProMath.infinityMinus ||
                screenPoints01 <= ProMath.infinityMinus) {
                return;
            }
            // limit price for stop limit order
            const limitPrice = !isNaN(this.priceValue)
                ? this.priceValue - (this.Order.StopLimit - this.Order.Price)
                : this.Order.Price;
            const cashItemSeriesSettings = this.myRenderer.chart.cashItemSeriesSettings;
            if (!this.TradeWasModified) {
                this.stopLimitY = Math.round(window.PointsConverter.GetScreenY(limitPrice));
                if (cashItemSeriesSettings) {
                    this.stopLimitY = Math.round(window.PointsConverter.GetScreenY(cashItemSeriesSettings.CalculateConverter(limitPrice)));
                }
            }
            const isVisibleStopOrder = CR.Contains(screenPoints00, screenPoints01) && !this.Order.isActivated;
            const isVisibleLimit = CR.Contains(screenPoints00, this.stopLimitY);
            // Some part of the order is not visible – apply a different style (important: before drawing the stop part)
            this.isLinkingStyle = (!isVisibleStopOrder && !this.Order.isActivated) || !isVisibleLimit;
            // Draw stop part
            if (this.Order.IsActivated) {
                // #92963
                this.MainOrderVisibility = isVisibleLimit;
                this.isDrawOrdersLinking = false;
                this.clearAllRectangles();
                // Otherwise, neither the limit nor the SL/TP can be moved
            }
            else {
                super.Draw(gr, window, param);
            }
            // Both parts of the order are not visible – do not draw anything
            if (!isVisibleStopOrder && !isVisibleLimit) {
                this.clearAllRectangles();
                return;
            }
            const separators = [];
            const hoveredOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None || this.isQtyChanging;
            const isBuy = this.Order.BuySell === OperationType.Buy;
            const startX = screenPoints00 + this.getPlateOffset();
            let curX = startX;
            const curY = this.stopLimitY - this.containerHeight / 2;
            this.limitIconRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
            curX += this.limitIconRect.Width;
            const limitIconText = 'LMT';
            curX = this.addTextRectWithSeparator(gr, limitIconText, curX, curY, separators, (r) => {
                this.limitIconTextRect = r;
            });
            //#region IsAllowOTHTrading
            if (this.isAllowOTHTrading) {
                this.overnightRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
                curX += this.imageRect.Width;
                separators.push(this.getVerticalSeparator(curX, curY));
            }
            else {
                this.overnightRect = Rectangle.Empty();
            }
            //#endregion IsAllowOTHTrading
            // Optimization – do not draw the order or add its label if it's outside
            if (this.isLinkingStyle) {
                const tooltipX = this.MainOrderVisibility ? startX : screenPoints00;
                let tooltipY = !this.MainOrderVisibility ? screenPoints01 : this.stopLimitY;
                const isTop = tooltipY < CR.Y;
                tooltipY = tooltipY >= CR.Y + CR.Height ? CR.Y + CR.Height : CR.Y;
                const descr = this.MainOrderVisibility ? this.LimitPriceDescription() : this.DescriptionText();
                this.linkedOrderToolTipRect = this.DrawLinkToOrder(gr, isTop, tooltipX, tooltipY, this.MainOrderVisibility ? OrderType.Limit : OrderType.StopLimit, descr);
                if (!isVisibleLimit) {
                    // Draw connection between parent order and linked tooltip
                    const verticalX = this.imageRect.X + this.imageRect.Width / 2;
                    const parentY = isTop ? this.imageRect.Y : this.imageRect.Y + this.imageRect.Height;
                    gr.DrawLines(TradingToolViewBase.sltpChartPen, [
                        new Point(verticalX, parentY),
                        new Point(verticalX, this.linkedOrderToolTipRect.Y + this.linkedOrderToolTipRect.Height / 2),
                        new Point(this.linkedOrderToolTipRect.X, this.linkedOrderToolTipRect.Y + this.linkedOrderToolTipRect.Height / 2)
                    ]);
                    this.infoRect = Rectangle.Empty();
                    this.removeButton = Rectangle.Empty();
                    this.slRect = Rectangle.Empty();
                    this.tpRect = Rectangle.Empty();
                    this.cleanLimitRectangles();
                    return;
                }
            }
            else {
                this.linkedOrderToolTipRect = Rectangle.Empty();
            }
            this.drawConnectionLine(gr);
            let quantity = '';
            let limitPriceStr = '';
            if (!this.Collapsed) {
                quantity = OrderFormatter.AmountStr(this.Order);
                curX = this.addTextRectWithSeparator(gr, quantity, curX, curY, separators, (r) => {
                    this.limitQuantityRect = r;
                });
                limitPriceStr = this.LimitPriceDescription();
                curX = this.addTextRectWithSeparator(gr, limitPriceStr, curX, curY, separators, (r) => {
                    this.limitInfoRect = r;
                });
                // #92963 нужно нарисовать крестик закрытия у лимит-части так как стоп-часть скрыта
                if (this.Order.IsActivated &&
                    !param.TerceraChart.TerceraChartTradingToolsRenderer.NotTradingContext &&
                    (this.AllowCancelOrder() || this.AllowClosePosition()) &&
                    !TradingLockUtils.TradingLock.tradingLocked) {
                    curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                        this.limitRemoveButton = r;
                    });
                }
                else {
                    this.limitRemoveButton = Rectangle.Empty();
                }
            }
            else {
                this.limitQuantityRect = Rectangle.Empty();
                this.limitInfoRect = Rectangle.Empty();
                this.limitRemoveButton = Rectangle.Empty();
            }
            this.limitContainerRect = new Rectangle(startX, curY, curX - startX, this.containerHeight);
            const { backgroundPen, backgroundBrush, TextBrush } = this.getDrawingTools(isBuy, hoveredOrSelected);
            gr.RoundRectangle(this.limitContainerRect, 4, backgroundBrush);
            const iconImg = hoveredOrSelected
                ? ThemeManager.NewImages.linkOrderOnChartHover
                : ThemeManager.NewImages.linkOrderOnChartDefault;
            gr.DrawCenteredImage(iconImg.image, this.limitIconRect);
            this.drawCenteredText(gr, limitIconText, this.limitIconTextRect, TradingToolViewBase.tradeFont, TextBrush);
            this.drawIcon(gr, ThemeManager.NewImages.tableOrderOvernightIcon.image, this.overnightRect);
            this.drawCenteredText(gr, quantity, this.limitQuantityRect, TradingToolViewBase.tradeFont, TextBrush);
            this.drawCenteredText(gr, limitPriceStr, this.limitInfoRect, TradingToolViewBase.tradeFont, TextBrush);
            const brush = this.getButtonBrush(isBuy, ToolSelectionType.Remove === this.CurrentSelection.CurrentHover);
            this.drawImgButton(gr, this.limitRemoveButton, ThemeManager.NewImages.closeIconDefault, ThemeManager.NewImages.closeIconHovered, brush, RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight));
            this.drawSeparators(gr, separators);
            if (this.Order.isActivated) {
                this.drawOrdersLinkingAtributes(gr, window, param);
            }
            if (!this.Collapsed) {
                this.DrawSLTP(gr, param, maxX, true, this.limitIconRect, hoveredOrSelected);
                this.DrawSLTP(gr, param, maxX, false, this.limitIconRect, hoveredOrSelected);
            }
            const priceLinePen = this.getPriceLinePen(isBuy, this.Order.isPosition);
            this.drawPriceLine(gr, this.limitContainerRect, maxX, this.stopLimitY, priceLinePen);
            const priceBrush = this.getPricePlateBrush(isBuy);
            this.tryAddPlateOnPriceScale(param, this.stopLimitPriceValue, this.Order.Price, priceBrush);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    GetCurrentSelection(x, y) {
        var _a;
        let newSelection = ToolSelectionType.None;
        newSelection = super.GetCurrentSelection(x, y);
        if (this.limitIconRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LimitIcon;
        }
        else if (this.limitIconTextRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LimitIcon;
        }
        else if (this.limitInfoRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LimitInfo;
        }
        else if (this.limitQuantityRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LimitQuantity;
        }
        else if (this.limitRemoveButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Remove;
        }
        else if (this.linkedOrderToolTipRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LinkTooltip;
        }
        else if (this.limitContainerRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Container;
        }
        else if (((_a = this.Order) === null || _a === void 0 ? void 0 : _a.IsActivated) && newSelection === ToolSelectionType.Line) {
            newSelection = ToolSelectionType.None;
        } // #92963
        return newSelection;
    }
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        this.TradeWasModified = true;
        this.errorMode = false;
        let data = window.PointsConverter.GetDataY(y);
        if (cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings) {
            data = cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings.RevertConverter(window.PointsConverter.GetDataY(y));
        }
        if (mouseDownHover === ToolSelectionType.LimitIcon || mouseDownHover === ToolSelectionType.LimitInfo) {
            // Move limit part
            if (this.AllowModify()) {
                this.stopLimitPriceValue = data;
                this.priceValue = this.Price;
                this.stopLimitY = y;
                // this.errorMode = this.Order.BuySell == OperationType.Buy ? this.stopLimitPriceValue < this.Order.StopLimit : this.stopLimitPriceValue > this.Order.StopLimit;
                this.stopLimitY = Math.floor(Math.round(window.PointsConverter.GetScreenY(this.stopLimitPriceValue)));
                if (cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings) {
                    this.stopLimitY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.stopLimitPriceValue))));
                }
                // дочерние сл/тп тулзы ставим на новые координаты когда двигается родительская
                if (
                /* this.Order.SLOrder != null || this.Order.TPOrder != null || */ (!isNaN(this.Order.StopLossPriceValue) ||
                    !isNaN(this.Order.TakeProfitPriceValue)) &&
                    this.AllowSLTP()) {
                    // Move child trades
                    const ins = this.Order.Instrument;
                    if (this.slTrade != null) {
                        const orderPrice = this.Order.Price; // this.Order.Price;
                        const slTicks = ins.calculateTicks(orderPrice, this.slTrade.Price - orderPrice);
                        const d = this.slTrade.Price - orderPrice;
                        const sign = d === 0 ? 0 : d > 0 ? 1 : -1;
                        // var correctPriceValue = this.stopLimitPriceValue; /*- (this.Order.StopLimit -this. Order.Price)*/
                        this.slTrade.priceValue = ins.calculatePrice(ins.roundPrice(this.stopLimitPriceValue), slTicks * sign);
                        this.slTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.slTrade.priceValue))));
                        this.slTrade.TradeWasModified = true;
                        this.slTrade.parentValue = this.stopLimitPriceValue;
                        this.slTrade.parentY = y;
                    }
                    if (this.tpTrade != null) {
                        const orderPrice = this.Order.Price; // this.Order.Price;
                        const tpTicks = ins.calculateTicks(orderPrice, this.tpTrade.Price - orderPrice);
                        const d = this.tpTrade.Price - orderPrice;
                        const sign = d === 0 ? 0 : d > 0 ? 1 : -1;
                        // let correctPriceValue = this.stopLimitPriceValue;
                        this.tpTrade.priceValue = ins.calculatePrice(ins.roundPrice(this.stopLimitPriceValue), tpTicks * sign);
                        this.tpTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.tpTrade.priceValue))));
                        this.tpTrade.TradeWasModified = true;
                        this.tpTrade.parentValue = this.stopLimitPriceValue;
                        this.tpTrade.parentY = y;
                    }
                    if (this.sllTrade != null) {
                        const orderPrice = this.Order.Price;
                        const sllTicks = ins.calculateTicks(orderPrice, this.sllTrade.Price - orderPrice);
                        const d = this.sllTrade.Price - orderPrice;
                        const sign = d === 0 ? 0 : d > 0 ? 1 : -1;
                        this.sllTrade.priceValue = ins.calculatePrice(ins.roundPrice(this.stopLimitPriceValue), sllTicks * sign);
                        this.sllTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.sllTrade.priceValue))));
                        this.sllTrade.TradeWasModified = true;
                        if (this.slTrade != null) {
                            this.sllTrade.parentValue = this.slTrade.priceValue;
                            this.sllTrade.parentY = this.slTrade.priceY;
                        }
                    }
                }
            }
        }
        else {
            // Move stop part & sl/tp
            const moveStopPart = mouseDownHover === ToolSelectionType.Image || mouseDownHover === ToolSelectionType.Info; // в этот else попадает не только move stop part а и move sl/tp - отделяем
            if (moveStopPart && this.Order.IsActivated) {
                return;
            } // #92963
            super.ProcessNewPosition(window, x, y, cashItemSeries);
            this.stopLimitPriceValue = !isNaN(this.priceValue)
                ? this.priceValue - (this.Order.StopLimit - this.Order.Price)
                : this.Order.Price;
            this.stopLimitPriceValue = this.Order.Instrument.roundPrice(this.stopLimitPriceValue);
            this.stopLimitY = Math.floor(Math.round(window.PointsConverter.GetScreenY(this.stopLimitPriceValue)));
            if (cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings) {
                this.stopLimitY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.stopLimitPriceValue))));
            }
            if (this.slTrade != null) {
                this.slTrade.parentY = this.stopLimitY;
            }
            if (this.tpTrade != null) {
                this.tpTrade.parentY = this.stopLimitY;
            }
        }
    }
    ProcessMoveFinish() {
        try {
            this.CurrentMovement.IsMovingRightNow = false;
            const mouseDownHover = this.CurrentSelection.MouseDownHover;
            if (this.errorMode) {
                this.CurrentMovement.IsMovingRightNow = false;
                this.slPlased = false;
                this.tpPlased = false;
                this.TradeWasModified = false;
                this.CancelMoving();
                this.ResetMouse();
                return;
            }
            // Limit part moving
            if (mouseDownHover === ToolSelectionType.LimitIcon || mouseDownHover === ToolSelectionType.LimitInfo) {
                // TODO  implement
                // let reset = FinishMove;
                if (this.Order != null) {
                    const newPrice = this.myRenderer.Instrument != null
                        ? this.myRenderer.Instrument.roundPrice(this.stopLimitPriceValue)
                        : this.stopLimitPriceValue;
                    const cancel = new CancelCB();
                    cancel.add(this.undoChanges.bind(this));
                    if (this.slTrade) {
                        this.slTrade.lastValidParentValue = newPrice;
                        cancel.add(this.slTrade.Updated.bind(this.slTrade));
                    }
                    if (this.tpTrade) {
                        this.tpTrade.lastValidParentValue = newPrice;
                        cancel.add(this.tpTrade.Updated.bind(this.tpTrade));
                    }
                    if (this.sllTrade) {
                        this.sllTrade.lastValidParentValue = newPrice;
                        cancel.add(this.sllTrade.Updated.bind(this.sllTrade));
                    }
                    // TODO  implement
                    this.lastValidLimitPriceValue = newPrice;
                    this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                        price: newPrice,
                        isLimitModify: true,
                        action: TerceraChartTradingOperation.MoveOrder,
                        CancelCallback: cancel.Run.bind(cancel)
                    });
                }
            }
            else // Stop part, sl/tp moving
             {
                const moveStopPart = mouseDownHover === ToolSelectionType.Image || mouseDownHover === ToolSelectionType.Info; // в этот else попадает не только move stop part а и move sl/tp
                if (moveStopPart && this.Order.IsActivated) {
                    return;
                } // #92963
                OrderToolView.prototype.ProcessMoveFinish.call(this);
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
            this.ResetMove();
            this.CurrentMovement.IsMovingRightNow = false;
            OrderToolView.prototype.ProcessMoveFinish.call(this);
        }
    }
    GetCursor(e) {
        const curElement = this.CurrentSelection.MouseDownHover === ToolSelectionType.None
            ? this.CurrentSelection.CurrentHover
            : this.CurrentSelection.MouseDownHover;
        switch (curElement) {
            case ToolSelectionType.Info:
            case ToolSelectionType.LimitIcon:
            case ToolSelectionType.LimitInfo:
                return this.AllowModify() ? Cursors.NoMoveVert : null;
            case ToolSelectionType.LimitQuantity:
                return Cursors.Hand;
            case ToolSelectionType.SL:
            case ToolSelectionType.TP:
                return this.AllowSLTP() ? (this.errorMode ? Cursors.No : Cursors.NoMoveVert) : null;
            case ToolSelectionType.Remove:
            case ToolSelectionType.Line:
            case ToolSelectionType.None:
            case ToolSelectionType.Trailing:
            default:
                return OrderToolView.prototype.GetCursor.apply(this);
        }
    }
    ResetMouse() {
        super.ResetMouse();
        this.stopLimitPriceValue = NaN;
        this.stopLimitY = -1;
    }
    ProcessClick(e) {
        if (this.CurrentSelection.MouseDownHover === this.CurrentSelection.CurrentHover) {
            switch (this.CurrentSelection.MouseDownHover) {
                case ToolSelectionType.LimitIcon:
                    this.Collapsed = !this.Collapsed;
                    break;
                case ToolSelectionType.LimitInfo:
                    this.Collapsed = !this.Collapsed;
                    break;
                case ToolSelectionType.LimitQuantity:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                            action: TerceraChartTradingOperation.ModifyOrderQuantity,
                            rect: this.limitQuantityRect,
                            quantity: this.Order.Amount,
                            orderToolView: this
                        });
                    }
                    break;
                default:
                    super.ProcessClick(e);
                    break;
            }
        }
        this.ResetMouse();
    }
    // Stop Price description
    DescriptionText() {
        const ins = this.Order.Instrument;
        const stopPrice = ins.roundPrice(this.TradeWasModified && !isNaN(this.priceValue) ? this.priceValue : this.Order.StopLimit);
        return ins.formatPrice(stopPrice);
    }
    CheckMinDistanceForStartMoving(x, y) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        if (!TradingLockUtils.TradingLock.tradingLocked &&
            (mouseDownHover == ToolSelectionType.LimitIcon || mouseDownHover == ToolSelectionType.LimitInfo) &&
            this.AllowModify()) {
            return Math.abs(this.CurrentMovement.PrevY - y) >= this.MinDistanceForStartMoving();
        }
        else {
            return super.CheckMinDistanceForStartMoving(x, y);
        }
    }
    QantityRect() {
        return this.limitQuantityRect;
    }
    LimitPriceDescription() {
        if (this.errorMode) {
            this.invalidPriceDescr = Resources.getResource('chart.visualTrading.Invalid price');
            return this.invalidPriceDescr;
        }
        else {
            const ins = this.Order.Instrument;
            const limitPrice = ins.roundPrice(this.TradeWasModified ? this.stopLimitPriceValue : this.Order.Price);
            return ins.formatPrice(limitPrice);
        }
    }
    Localize() {
        super.Localize();
        this.invalidPriceDescr = Resources.getResource('chart.visualTrading.Invalid price');
    }
    get PriceMin() {
        if (!this.Order)
            return null;
        return Math.min(this.Order.StopLimit, this.Order.Price);
    }
    get PriceMax() {
        if (!this.Order)
            return null;
        return Math.max(this.Order.StopLimit, this.Order.Price);
    }
    getToolTipDescription() {
        return this.Order.isActivated ? this.LimitPriceDescription() : this.DescriptionText();
    }
    getDrawingTools(isBuy, hoveredOrSelected) {
        let { backgroundPen, backgroundBrush, TextBrush } = super.getDrawingTools(isBuy, hoveredOrSelected);
        if (!this.MainOrderVisibility) {
            backgroundBrush = hoveredOrSelected
                ? TradingToolViewBase.linkedStyleHoverBrush
                : TradingToolViewBase.linkedStyleDefaultBrush;
            backgroundPen = Pens.Black;
            TextBrush = TradingToolViewBase.linkedStyleFontBrush;
        }
        return { backgroundPen, backgroundBrush, TextBrush };
    }
    getButtonBrush(isBuy, isHovered, isActive) {
        if (!this.MainOrderVisibility) {
            return isHovered ? TradingToolViewBase.linkedTradeToolButtonBackgroundHoveredBrush : null;
        }
        else {
            return super.getButtonBrush(isBuy, isHovered, isActive);
        }
    }
    drawConnectionLine(gr) {
        if (this.Order.IsActivated) {
            // #92963
            return;
        }
        const lineLenght = this.indentationLineLength;
        let parentY = 0;
        if (this.mainContainer.Y + this.mainContainer.Height / 2 <
            this.limitContainerRect.Y + this.limitContainerRect.Height / 2) {
            parentY = this.linkedOrderToolTipRect.IsEmpty()
                ? this.mainContainer.Bottom()
                : this.linkedOrderToolTipRect.Bottom();
        }
        else {
            parentY = this.linkedOrderToolTipRect.IsEmpty()
                ? this.mainContainer.Top()
                : this.linkedOrderToolTipRect.Top();
        }
        gr.DrawLine(TradingToolViewBase.sltpChartPen, this.limitIconRect.X - lineLenght, this.limitIconRect.Y + this.limitIconRect.Height / 2, this.limitIconRect.X - lineLenght, parentY);
        gr.DrawLine(TradingToolViewBase.sltpChartPen, this.limitIconRect.X - lineLenght, this.limitIconRect.Y + this.limitIconRect.Height / 2, this.limitIconRect.X, this.limitIconRect.Y + this.limitIconRect.Height / 2);
    }
    cleanLimitRectangles() {
        this.limitRemoveButton = Rectangle.Empty();
        this.limitIconTextRect = Rectangle.Empty();
        this.limitGripRect = Rectangle.Empty();
        this.limitIconRect = Rectangle.Empty();
        this.limitQuantityRect = Rectangle.Empty();
        this.limitInfoRect = Rectangle.Empty();
    }
}
